import React, { useState, useEffect } from "react";
import SideBar from "../components/SideBar";
import TopBar from "../components/TopBar";
import inter from "../assets/image/inter.svg";
import note from "../assets/image/note.svg";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Prayer() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    request: "",
    is_shared_anonymously: false,
  });
  const [loading, setLoading] = useState(false);
  const [intercessoryResponses, setIntercessoryResponses] = useState([]);
  const [fetchingResponses, setFetchingResponses] = useState(false);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const fetchIntercessoryResponses = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      console.warn("No auth token found for fetching intercessory responses");
      return;
    }

    setFetchingResponses(true);
    try {
      const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;

      const res = await fetch(
        `${TEST_BASE_URL}/intercessory-response-status`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );


      const data = await res.json();

      if (!res.ok) {
        console.error("Error fetching intercessory responses:", data);
        toast.error(data.message || "Failed to fetch intercessory responses.", {
          theme: "colored",
        });
        return;
      }

      const responseData = data.data;
      
      setIntercessoryResponses(responseData);
    } catch (err) {
      console.error("Network error fetching intercessory responses:", err);
      toast.error("Network error — please try again.", { theme: "colored" });
    } finally {
      setFetchingResponses(false);
    }
  };

  useEffect(() => {
    fetchIntercessoryResponses();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.request) {
      toast.error("Please enter your prayer request.", { theme: "colored" });
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      toast.error("You must be logged in to send a prayer request.", {
        theme: "colored",
      });
      return;
    }

    setLoading(true);
    try {
      const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;

      const res = await fetch(
        `${TEST_BASE_URL}/prayer-requests`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            request: formData.request,
            is_shared_anonymously: formData.is_shared_anonymously,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        if (data.errors) {
          console.error("Validation errors:", data.errors);
          Object.entries(data.errors).forEach(([field, messages]) => {
            messages.forEach((msg) => toast.error(msg, { theme: "colored" }));
          });
        } else {
          console.error("Error response:", data);
          toast.error(data.message || "Failed to send prayer request.", {
            theme: "colored",
          });
        }
        return;
      }

      toast.success("Prayer request submitted successfully!", {
        theme: "colored",
      });
      setFormData({ request: "", is_shared_anonymously: false });
      await fetchIntercessoryResponses();
    } catch (err) {
      console.error("Error submitting prayer request:", err);
      toast.error("Network error — please try again.", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="dashboard">
      <SideBar isOpen={sidebarOpen} />
      <div className="body-content">
        <TopBar onToggleSidebar={toggleSidebar} />
        <div className="bottom-content">
          <div className="prayer-title">
            <span>Prayer & Support Network</span>
            <a href="/prayerhistory">
              <u>View Prayer Requests</u>
            </a>
          </div>
          <div className="burdens-card">
            <div className="col-md-5 px-2">
              <h5 className="burdens-card-head">
                Share Your Burdens. Receive Prayer.
              </h5>
              <p>
                We believe in the power of prayer and the strength of community.
                This space allows you to share your prayer requests and trust
                that our intercessory teams will stand with you in faith and
                love.
              </p>

              <img src={inter} alt="intercessory" />
              <h6>Intercessory Responses</h6>
              <p>
                Once you've submitted your request, our dedicated intercessory
                team will begin praying over it. You may receive words of
                encouragement, scriptures, or messages of support right here on
                the portal.
              </p>

              {fetchingResponses ? (
                <p>Loading responses...</p>
              ) : intercessoryResponses?.intercessory_response_enabled ? (
                <div className="responses-list">
                  <p style={{ color: "green", fontWeight: "bold" }}>
                    Intercessory responses are enabled
                  </p>
                </div>
              ) : (
                <p>Intercessory responses are currently not available.</p>
              )}

              <img src={note} alt="note" />
              <div className="feedback-note">
                <h6>Notes</h6>
                <ul>
                  <li>All prayer requests are reviewed before being shared.</li>
                  <li>You may choose to remain anonymous.</li>
                </ul>
              </div>
            </div>

            <div className="col-md-5">
              <div className="submit-card">
                <h5>Submit a Prayer Request</h5>
                <form onSubmit={handleSubmit}>
                  <div className="sharediv">
                    <input
                      type="checkbox"
                      name="is_shared_anonymously"
                      checked={formData.is_shared_anonymously}
                      onChange={handleChange}
                    />
                    <span>Share Anonymously</span>
                  </div>

                  <label className="pb-2">Your Prayer Request</label>
                  <textarea
                    name="request"
                    cols="30"
                    rows="9"
                    className="form-control"
                    placeholder="Enter your prayer request"
                    value={formData.request}
                    onChange={handleChange}
                  ></textarea>

                  <p className="pt-2">
                    <small>
                      Please read our{" "}
                      <a
                        href="https://www.rccgbournemouth.org/privacy-notice"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="privacy-link"
                      >
                        <small>
                          <u>Privacy Notice</u>
                        </small>
                      </a>{" "}
                      to learn how we protect your personal data.
                    </small>
                  </p>

                  <div className="sharebutton">
                    <button
                      type="submit"
                      className="text-white"
                      disabled={loading}
                      style={{ background: "transparent", fontSize: "14px" }}
                    >
                      {loading ? "Submitting..." : "Share Your Prayer Request"}
                    </button>
                  </div>
                </form>
              </div>

              <div className="feedback_note">
                <h6>Notes</h6>
                <div>
                  <li>All prayer requests are reviewed before being shared.</li>
                  <li>You may choose to remain anonymous.</li>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Prayer;